import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isFuture } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCalendar } from '@sorare/core/src/atoms/icons/system/iconCalendar.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { TooltipGame } from '#src/components/player/AnyPlayerUpcomingGames/TooltipGame/index.tsx';

import type {
  CalendarTooltipQuery,
  CalendarTooltipQueryVariables,
  CalendarTooltip_anyPlayer,
} from './__generated__/index.graphql.ts';

const ScrollPaper = styled(Vertical)`
  max-height: 350px;
  width: 300px;
  overflow-y: auto;
`;
const Calendar = styled(Chip)`
  width: var(--triple-unit);
  border-radius: var(--triple-unit);
`;

const CALENDAR_TOOLTIP_QUERY = gql`
  query CalendarTooltipQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      activeClub {
        slug
        upcomingAnyGames(first: 5) {
          id
          date
          ...TooltipGame_anyGame
        }
      }
      activeNationalTeam {
        slug
        upcomingAnyGames(first: 5) {
          id
          date
          ...TooltipGame_anyGame
        }
      }
    }
  }
  ${TooltipGame.fragments.anyGame}
` as TypedDocumentNode<CalendarTooltipQuery, CalendarTooltipQueryVariables>;

type Props = {
  anyPlayer: CalendarTooltip_anyPlayer;
  hasAGame: boolean;
};

const UpcomingGames = ({ playerSlug }: { playerSlug: string }) => {
  const { data, loading } = useQuery(CALENDAR_TOOLTIP_QUERY, {
    variables: {
      slug: playerSlug,
    },
    skip: !playerSlug,
  });
  const clubUpcomingGames =
    data?.anyPlayer.activeClub?.upcomingAnyGames.filter(
      anyGame => anyGame && isFuture(anyGame.date)
    ) || Array.from({ length: 5 });

  const nationalTeamUpcomingGames =
    data?.anyPlayer.activeNationalTeam?.upcomingAnyGames.filter(
      anyGame => anyGame && isFuture(anyGame.date)
    ) || Array.from({ length: 5 });

  const upcomingGames = [...clubUpcomingGames, ...nationalTeamUpcomingGames];
  const sortedUpcomingGames = upcomingGames
    .sort((a, b) => {
      if (a.date && b.date) {
        return a.date.getTime() - b.date.getTime();
      }
      return 0;
    })
    .slice(0, 5);

  return (
    <ScrollPaper>
      <Skeleton loading={loading}>
        <BodyS color="var(--c-nd-600)">
          <FormattedMessage
            defaultMessage="{nbGames, plural, one {Next # game} other {Next # games}}"
            values={{ nbGames: sortedUpcomingGames.length }}
          />
        </BodyS>
      </Skeleton>
      {sortedUpcomingGames.map((anyGame, index) => (
        <Skeleton key={anyGame?.id || index} loading={loading}>
          <TooltipGame anyGame={anyGame} />
        </Skeleton>
      ))}
    </ScrollPaper>
  );
};

export const CalendarTooltip = ({ anyPlayer, hasAGame }: Props) => {
  if (!hasAGame) return null;
  return (
    <Tooltip title={<UpcomingGames playerSlug={anyPlayer.slug} />}>
      <Calendar size="medium" color="transparent">
        <FontAwesomeIcon
          aria-label="calendar"
          icon={iconCalendar}
          size="sm"
          color="var(--c-nd-600)"
        />
      </Calendar>
    </Tooltip>
  );
};

CalendarTooltip.fragments = {
  anyPlayer: gql`
    fragment CalendarTooltip_anyPlayer on AnyPlayerInterface {
      slug
    }
  ` as TypedDocumentNode<CalendarTooltip_anyPlayer>,
};
